// Activity log — dual-shape helpers for V1↔V2 round-trip compatibility.
//
// V1 (public/app/index.html:5861-5862) writes activity entries as:
//   { id: Date.now(), ts: new Date().toISOString(), user, email,
//     category, action, detail }
// V2 writes:
//   { ts: Date.now() /* number */, actor, kind, target?, meta? }
//
// Writers use makeActivityLogEntry() which populates BOTH shapes so that
// when V1 reads a V2-written entry (or vice versa) the row renders normally.
// Readers use normalizeActivityEntry() to flatten either shape into a single
// canonical view for display / filtering.

import type { ActivityLogEntry } from '../types'

/** Compose an activity log entry that satisfies both V1 and V2 readers.
 *  - V2 fields: ts (ms), actor, kind, target, meta.desc.
 *  - V1 aliases: id, ts (ISO), user, email, category (=kind),
 *    action (=desc), detail (=meta.detail or '').
 *  V1's activityLog feed falls back to the `action` / `detail` pair when
 *  rendering, so writers MUST populate those or V1 rows show blank. */
export function makeActivityLogEntry(
  uid: string,
  kind: string,
  target: number | string | undefined,
  desc: string,
  meta?: Record<string, unknown>,
): ActivityLogEntry {
  const now = Date.now()
  const iso = new Date(now).toISOString()
  const metaMerged: Record<string, unknown> = { desc, ...(meta ?? {}) }
  const detailFromMeta =
    typeof meta?.detail === 'string' ? (meta.detail as string) : ''
  const entry: ActivityLogEntry = {
    // V1 legacy shape
    id: now,
    // V2 canonical ts is numeric ms. V1 reads activityLog.ts.split('T')[0]
    // (public/app/index.html:5886) which only works with ISO strings; so we
    // store an ISO string and V2 readers coerce via new Date(ts).getTime().
    ts: iso,
    user: uid,
    email: uid,
    category: kind,
    action: desc,
    detail: detailFromMeta,
    // V2 canonical fields
    actor: uid,
    kind,
    meta: metaMerged,
  }
  if (target != null) entry.target = String(target)
  return entry
}

export interface NormalizedActivityEntry {
  ts: number            // always ms epoch
  kind: string          // best-guess
  desc: string          // primary description
  actor: string         // uid / email / 'system'
  target?: string
}

/** Read helper — accepts either V1 or V2 shape and returns a canonical view.
 *  Use in RecentActivitySection (Options.tsx) and any other reader. */
export function normalizeActivityEntry(e: ActivityLogEntry): NormalizedActivityEntry {
  // ts: V1 writes ISO string; V2 writes number ms. Both need → ms.
  let tsMs: number
  if (typeof e.ts === 'number') {
    tsMs = e.ts
  } else if (typeof e.ts === 'string') {
    const parsed = Date.parse(e.ts)
    tsMs = Number.isFinite(parsed) ? parsed : 0
  } else {
    tsMs = 0
  }

  // kind: prefer V2 kind, fall back to V1 category.
  const kind = e.kind || e.category || ''

  // desc: V2 writers put description in meta.desc. V1 puts it in `action`.
  // Also fall back to detail / kind so the row is never blank.
  const metaDesc =
    e.meta && typeof (e.meta as Record<string, unknown>).desc === 'string'
      ? ((e.meta as Record<string, unknown>).desc as string)
      : ''
  const desc = metaDesc || e.action || e.detail || kind

  const actor = e.actor || e.user || e.email || ''

  const out: NormalizedActivityEntry = { ts: tsMs, kind, desc, actor }
  if (e.target != null) out.target = e.target
  return out
}
